import React, { useEffect, useMemo, useRef } from 'react'

import { useAdInFeed } from '@expPlatform/embeds/ads/hooks/useAdInFeed'
import { useAdsOnly } from '@expPlatform/embeds/ads/hooks/useAdsOnly'
import { putAdsToPositionInFeed } from '@expPlatform/embeds/ads/utils/putAdsToPositionInFeed'

import { dispatchWidgetRenderOutcome } from '@core/customEvent'
import { useFeedQueryContext } from '@core/feed'
import type {
  EmbedFeedParams,
  IAnyWidgetParams,
  IWidgetInitialParams,
} from '@core/types'
import { WidgetSizeEnum } from '@core/types'
import { WidgetWrapper } from '@core/widgets'
import { EmbedFeedStoreProvider } from '@core/widgets/embedFeed/store'
import { useStore } from '@core/widgets/store'
import {
  actionsSelector,
  appContextSelector,
  paramsSelector,
} from '@core/widgets/store/selectors'
import { Grid } from '@embed/components/Grid'
import { AppContextType } from '@embed/components/helpers'
import type { EmbedFeedAppContext } from '@embed/hooks/useEmbedAppContext'
import { GlobalEmbedCustomCSS } from '@embed/styles/GlobalEmbedCustomCSS'

const EmbedFeedComponent: React.FunctionComponent = () => {
  const appContext = useStore(appContextSelector) as EmbedFeedAppContext | null
  const params = useStore(paramsSelector) as EmbedFeedParams
  const { setVideos } = useStore(actionsSelector)

  const [{ loading, videos: feedVideos, playlists, error }, feedActions] =
    useFeedQueryContext()

  const isAdsOnly = useAdsOnly({ appContext })

  const {
    adVideos,
    positions,
    loading: adVideosLoading,
  } = useAdInFeed({
    appContext,
    params,
  })

  const videos = useMemo(() => {
    if (isAdsOnly) {
      return adVideos
    }

    return putAdsToPositionInFeed({
      adVideos,
      videos: feedVideos,
      positions,
    })
  }, [isAdsOnly, adVideos, feedVideos, positions])

  useEffect(() => {
    if (!loading && !adVideosLoading) {
      setVideos(videos)
    }
  }, [loading, adVideosLoading, videos, setVideos])

  const isPlaylistOrHasVideos = useMemo(() => {
    return !!playlists.length || !!videos.length
  }, [videos.length, playlists.length])

  const renderOutcomeDispatched = useRef(false)
  useEffect(() => {
    if (renderOutcomeDispatched.current || loading || adVideosLoading) {
      return
    }
    renderOutcomeDispatched.current = true
    const hasFeedError = error !== undefined && error !== null
    if (hasFeedError && !isPlaylistOrHasVideos) {
      dispatchWidgetRenderOutcome({
        target: params.target,
        detail: {
          type: 'embed-feed',
          status: 'error',
          reason: error instanceof Error ? error.message : String(error),
        },
      })
      return
    }
    dispatchWidgetRenderOutcome({
      target: params.target,
      detail: {
        type: 'embed-feed',
        status: isPlaylistOrHasVideos ? 'ready' : 'empty',
      },
    })
  }, [adVideosLoading, error, isPlaylistOrHasVideos, loading, params.target])

  if (isAdsOnly && !isPlaylistOrHasVideos) {
    return null
  }

  // Skip rendering (collapse) the widget if there are no videos to show and
  // there are no playlists
  if (!loading && !adVideosLoading && !isPlaylistOrHasVideos) {
    return null
  }

  return (
    <>
      <GlobalEmbedCustomCSS params={params} />
      <Grid
        loading={loading}
        appContext={appContext}
        params={params}
        videos={videos}
        playlists={playlists}
        fetchMoreVideos={feedActions.loadMore}
        largeCarousel={
          params.size === WidgetSizeEnum.LARGE && params.mode !== 'pinned'
        }
      />
    </>
  )
}

/**
 *
 */
const EmbedFeed: React.FC<{
  initialParams: IWidgetInitialParams
  widgetParams: IAnyWidgetParams
}> = ({ initialParams, widgetParams }) => (
  <WidgetWrapper
    appContextType={AppContextType.EMBED_GRID_CONTEXT_TYPE}
    initialParams={initialParams}
    widgetParams={widgetParams}
  >
    <EmbedFeedStoreProvider>
      <EmbedFeedComponent />
    </EmbedFeedStoreProvider>
  </WidgetWrapper>
)

export default EmbedFeed
